import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import ProductDetailModal from "./ProductDetailModal";
import LoadingAM from "../assets/3dgifmaker93408.gif";

export const ProductGridmainSection = () => {
  const { id, main_cat_id } = useParams();
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const [selectedCategoryData, setSelectedCategoryData] = useState();
  const [totalProducts, setTotalProducts] = useState(0);

  const [seoData, setSeoData] = useState({
    title: "Professional Products Pattern",
    description:
      "Discover our comprehensive collection of professional products with advanced patterns and designs.",
    keywords: "professional products, patterns, designs, quality products",
    canonicalUrl: window.location.href,
  });

  useEffect(() => {
    setSelectedCategory(id);
  }, [id]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(
        `https://webadmin.heritagetextures.in/api/get_category?main_cat_id=${main_cat_id}`,
      );
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      setCategories(data?.data || []);
    } catch (err) {
      setError(err.message);
    }
  }, [main_cat_id]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchProducts = useCallback(
    async (page = 1) => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `https://webadmin.heritagetextures.in/api/getproduct?main_cat_id=${main_cat_id}&cat_id=${
            selectedCategory || id
          }&page=${page}`,
        );
        if (!response.ok) throw new Error("Failed to fetch products");
        const data = await response.json();
        setProducts(data.data?.data || []);
        setTotalPages(data.data?.last_page || 1);
        setTotalProducts(data.data?.total || 0);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedCategory, id, main_cat_id],
  );

  useEffect(() => {
    fetchProducts(currentPage);
  }, [fetchProducts, currentPage]);

  const memoizedProducts = useMemo(() => products, [products]);

  useEffect(() => {
    if (selectedCategory && categories.length > 0) {
      const selectedCatData = categories.find(
        (cat) => cat.id === parseInt(selectedCategory),
      );
      if (selectedCatData) {
        setSelectedCategoryData(selectedCatData);
        setSeoData({
          title: `${selectedCatData.name} - Professional Products Pattern`,
          description:
            selectedCatData.description ||
            `Browse our ${selectedCatData.name} collection with ${totalProducts} professional products.`,
          keywords: `${selectedCatData.name}, professional products, patterns, ${selectedCatData.name} designs`,
          canonicalUrl: window.location.href,
        });
      }
    } else if (categories.length > 0) {
      setSeoData({
        title: "Professional Products Pattern - All Categories",
        description: `Explore our complete collection of ${totalProducts} professional products across all categories.`,
        keywords:
          "professional products, all categories, patterns, designs, quality products",
        canonicalUrl: window.location.href,
      });
    }
  }, [selectedCategory, categories, totalProducts]);

  useEffect(() => {
    // Update the document title
    document.title = seoData.title;

    // Update meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const metaCanonical = document.querySelector('link[rel="canonical"]');

    if (metaDescription) {
      metaDescription.setAttribute("content", seoData.description);
    }

    if (metaKeywords) {
      metaKeywords.setAttribute("content", seoData.keywords);
    }

    if (metaCanonical) {
      metaCanonical.setAttribute("href", seoData.canonicalUrl);
    }

    // Generate structured data for SEO
    const generateStructuredData = () => {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: seoData.title,
        description: seoData.description,
        url: seoData.canonicalUrl,
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: totalProducts,
          itemListElement: memoizedProducts.map((product, index) => ({
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "Product",
              name: product.title,
              description: product.description || product.title,
              image: product.image,
              category: product.category,
              url: `${window.location.origin}/product/${
                product.id
              }/${currentPage}/${main_cat_id}/${selectedCategory || id}/${
                product.title
              }`,
            },
          })),
        },
      };

      if (selectedCategoryData) {
        structuredData.mainEntity.about = {
          "@type": "Thing",
          name: selectedCategoryData.name,
          description: selectedCategoryData.description,
        };
      }

      return JSON.stringify(structuredData);
    };

    // Insert structured data into the head
    let script = document.getElementById("structured-data");
    if (!script) {
      script = document.createElement("script");
      script.id = "structured-data";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.text = generateStructuredData();
  }, [
    seoData,
    memoizedProducts,
    totalProducts,
    selectedCategoryData,
    currentPage,
    main_cat_id,
    selectedCategory,
    id,
  ]);

  if (isLoading)
    return (
      <div className="flex items-center justify-center min-h-[80vh] w-full py-24">
        <div className="text-center">
          <img
            src={LoadingAM}
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4"
            alt="Loading products..."
            width="80"
            height="80"
          />
          <p className="text-gray-600 text-sm sm:text-base" aria-live="polite">
            Loading products...
          </p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Error Loading Products
          </h2>
          <p className="text-red-500 text-sm sm:text-base">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );

  return (
    <main className="container min-h-[67vh] py-8 sm:py-12 md:py-16 lg:py-20 mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-24">
      {/* Page Header */}
      <header className="mb-8 sm:mb-10 md:mb-12">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-2">
          Professional Products Pattern
        </h1>
        {totalProducts > 0 && (
          <p className="text-sm sm:text-base text-gray-600">
            Showing {memoizedProducts.length} of {totalProducts} products
            {currentPage > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </p>
        )}
      </header>

      {/* Selected Category Information */}
      {selectedCategoryData && (
        <section className="mb-8 sm:mb-10 md:mb-12 p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                {selectedCategoryData.name}
              </h2>
              {selectedCategoryData.description && (
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {selectedCategoryData.description}
                </p>
              )}
            </div>
            {selectedCategoryData.image && (
              <div className="lg:w-48 lg:flex-shrink-0">
                <img
                  src={selectedCategoryData.image}
                  alt={`${selectedCategoryData.name} category`}
                  className="w-full h-32 sm:h-40 lg:h-32 object-cover rounded-lg shadow-md"
                  loading="lazy"
                  width="192"
                  height="128"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Filter Section */}
      <section className="mb-8 sm:mb-10 md:mb-12" aria-label="Product filters">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <label
              htmlFor="categoryFilter"
              className="text-sm sm:text-base text-gray-900 font-semibold"
            >
              Filter by Pattern:
            </label>
            <select
              id="categoryFilter"
              className="w-full sm:w-auto border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
              aria-label="Filter products by category"
            >
              <option value={0}>All Categories</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category?.name}
                </option>
              ))}
            </select>
          </div>

          {/* Results summary for mobile */}
          <div className="text-sm text-gray-600 sm:hidden">
            {memoizedProducts.length} results
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="mb-8 sm:mb-10 md:mb-12" aria-label="Products grid">
        {memoizedProducts.length === 0 ? (
          <div className="text-center py-12 sm:py-16 md:py-20">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              No products found
            </h3>
            <p className="text-sm sm:text-base text-gray-600">
              Try adjusting your filters or check back later.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
            {memoizedProducts.map((product) => (
              <article
                key={product.id}
                className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
                itemScope
                itemType="https://schema.org/Product"
              >
                <div className="relative mb-3 sm:mb-4 overflow-hidden">
                  <a
                    href={`/product/${
                      product.id
                    }/${currentPage}/${main_cat_id}/${
                      selectedCategory || id
                    }/${encodeURIComponent(product.title)}`}
                    className="block relative"
                    aria-label={`View details for ${product.title}`}
                    title={`View ${product.title} details`}
                  >
                    {imageLoading[product.id] !== false ? (
                      <div className="flex items-center justify-center w-full h-48 sm:h-56 md:h-64 bg-gray-100">
                        <img
                          src={product.image}
                          alt={
                            product.title
                              ? `${product.title} product image`
                              : "Product image"
                          }
                          loading="lazy"
                          width="300"
                          height="300"
                          className="w-full h-48 sm:h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                          itemProp="image"
                          onLoad={() =>
                            setImageLoading((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }))
                          }
                          onError={(e) => {
                            e.target.src = "/api/placeholder/300/300";
                            setImageLoading((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }));
                          }}
                        />
                        {/* <img
                          src={LoadingAM}
                          className="w-12 h-12 sm:w-16 sm:h-16"
                          alt="Loading product image"
                          width="64"
                          height="64"
                        /> */}
                      </div>
                    ) : (
                      <>
                        <img
                          src={product.image}
                          alt={
                            product.title
                              ? `${product.title} product image`
                              : "Product image"
                          }
                          loading="lazy"
                          width="300"
                          height="300"
                          className="w-full h-48 sm:h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                          itemProp="image"
                          onLoad={() =>
                            setImageLoading((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }))
                          }
                          onError={(e) => {
                            e.target.src = "/api/placeholder/300/300";
                            setImageLoading((prev) => ({
                              ...prev,
                              [product.id]: false,
                            }));
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity duration-300" />
                      </>
                    )}
                  </a>
                </div>

                <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                  <h3
                    className="text-sm sm:text-base md:text-lg font-semibold mb-1 sm:mb-2 text-gray-900 line-clamp-2 group-hover:text-orange-700 transition-colors duration-200"
                    itemProp="name"
                  >
                    {product.title}
                  </h3>
                  {product.category && (
                    <p
                      className="text-xs sm:text-sm text-gray-600"
                      itemProp="category"
                    >
                      {product.category}
                    </p>
                  )}

                  {/* Hidden structured data */}
                  <div style={{ display: "none" }}>
                    <span itemProp="description">
                      {product.description || product.title}
                    </span>
                    <span itemProp="url">{`/product/${
                      product.id
                    }/${currentPage}/${main_cat_id}/${
                      selectedCategory || id
                    }/${encodeURIComponent(product.title)}`}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
          aria-label="Pagination Navigation"
        >
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-orange-600 text-white hover:bg-orange-700 hover:shadow-md active:scale-95"
              }`}
              aria-label="Go to previous page"
            >
              Previous
            </button>

            <div className="flex items-center gap-1 mx-2 sm:mx-4">
              <span className="px-2 sm:px-3 py-1 sm:py-2 font-bold text-gray-900 text-sm sm:text-base">
                {currentPage}
              </span>
              <span className="text-gray-500 text-sm sm:text-base">of</span>
              <span className="px-2 sm:px-3 py-1 sm:py-2 font-bold text-gray-900 text-sm sm:text-base">
                {totalPages}
              </span>
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || !products.length}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 ${
                currentPage === totalPages || !products.length
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-orange-600 text-white hover:bg-orange-700 hover:shadow-md active:scale-95"
              }`}
              aria-label="Go to next page"
            >
              Next
            </button>
          </div>

          {/* Page info for mobile */}
          <div className="text-xs sm:text-sm text-gray-600 sm:hidden">
            Page {currentPage} of {totalPages}
          </div>
        </nav>
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </main>
  );
};
